// Analysis screen — the pure half: every number this screen prints that is
// DERIVED from the /api/analysis payload rather than read straight off it.
// Percentages and their pill colours, pence-in-the-pound, the chart scales,
// the month labels, and every computed sentence live here so they are tested
// on fixtures (scripts/analysis-view.test.mjs) rather than read off a rendered
// page. No DOM: the screen module owns the drawing. One import
// (business-settings, for the short name fixItems() prints) — everything
// else is still pure arithmetic on the payload.
//
// The payload shape is the contract in docs/ANALYSIS-CONTRACT.md. Every figure on
// the screen is computed from it; nothing is hard-coded from the mockup.

import { currencySymbol, dayMonth, minorUnit, weekdayDayMonth } from './round-config'
import { businessSettings, type BusinessSettings } from './business-settings'

// ---- the payload -------------------------------------------------------------

export interface AnalysisLedger {
  gross_pence: number
  worker_pence: number
  kept_pence: number
  days_filled: number
}

export interface AnalysisMonth {
  month: string // 'YYYY-MM'
  cleans: number
  gross_pence: number
  unpriced: number
  days_worked: number
  ledger: AnalysisLedger | null
}

export interface AnalysisKeptMonth extends AnalysisLedger {
  month: string
  days_worked: number
}

// One working day that has been closed out: the row the old spreadsheet's
// "Day's Total" tab held, which is the figure the tax return is built from.
export interface AnalysisDay {
  id?: number // the day total's own row and version, for changing it from Day totals
  version?: number
  paid_unknown?: true // pay or kept never entered (shown as £0)
  kept_unknown?: true
  date: string
  worker: string | null
  gross_pence: number
  worker_pence: number
  kept_pence: number
  cleans: number
  notes: string | null
  // Close out the day (0095): paid, how, and the cash handed in.
  paid_at?: string | null
  paid_how?: string | null
  cash_in_at?: string | null
  // A day from before Close out the day said to be not paid after all (0117): still to pay until ticked.
  pay_open_at?: string | null
  // An owner's own pay from before (0097): what the owner took, never worker pay.
  owner_amount?: true
}

// Close out the day (0095): per day and person, customers' money still out and the cash they took.
export interface AnalysisDayMoney { date: string; worker: string; owed_pence: number; cash_pence: number; cash_in: boolean; work_pence?: number }

// A day's work with no day total against it, keyed per (date, worker) the way
// day_ledger is. A day two people worked and only one totalled is half-done.
export interface AnalysisMissingDay {
  date: string
  worker: string
  cleans: number
  pence: number
}

export interface AnalysisYear {
  year: number
  gross_pence: number
  cleans: number
  unpriced: number
  avg_pence: number | null
  new_customers: number
  to_date: boolean
  prev_same_point_pence: number | null
}

export interface AnalysisHealth {
  active: number
  paused: number
  lapsed: number
  active_with_cycle: number
  active_no_cycle: number
  book_customers: number
  book_pence_per_year: number
  overdue_count: number
  overdue_value_pence: number
  adherence: null | { customers: number; expected: number; done: number; pct: number }
  winback: { count: number; value_pence_per_year: number }
}

export interface AnalysisOrigin {
  origin: 'Canvassing' | 'Website' | 'Google Ads' | 'Referral' | 'Not recorded' | 'Other'
  customers: number
  active: number
  income_year_pence: number
}

export interface AnalysisFixes {
  no_cycle: number
  no_origin: number
  unpriced_year: number
  ledger_missing_days: number
}

export interface AnalysisPayload {
  generated_at: string
  today: string
  // Money brought from Squeegee (0043), only on a round that has it: this year's money, each figure apart.
  money?: { received_pence: number; outstanding_pence: number; owed_on_visits_pence: number; owed_on_accounts_pence: number; credit_pence: number; collecting_pence: number }
  headline: {
    ytd_pence: number
    ytd_cleans: number
    ytd_prev_pence: number
    ytd_prev_cleans: number
    last12_pence: number
    prev12_pence: number
    kept_month: AnalysisKeptMonth | null
    owed_pence: number
    owed_count: number
  }
  months: AnalysisMonth[]
  days: AnalysisDay[]
  day_money?: AnalysisDayMoney[]
  // The names of the round's owners who do jobs (Close out the day: their own pay and cash are never owed).
  owners?: string[]
  // Two or more in one van (0096): each day's riders (not the leads).
  day_crews?: { date: string; lead: string; person: string }[]
  // How the owners get paid and what each took (0097): a day (YYYY-MM-DD) or a month's wage (YYYY-MM).
  owner_takes?: { id: number; version: number; period: string; payee: string; category: 'share' | 'wage' | 'took'; amount_pence: number; paid_at: string | null; paid_how: string | null }[]
  owner_pay?: { mode: 'keep' | 'wage' | 'split' | 'ask'; base: 'after' | 'all'; wage_kind: 'day' | 'month' | null; wage_pence: number | null }
  pay_partners?: { id: number; name: string; pct: number; min_pence: number | null }[]
  missing_days: AnalysisMissingDay[]
  // One-person rounds only (0078): days the owner finished with Finish my day and entered no day total. They count
  // as having their total, so they are not in missing_days.
  finished_days?: AnalysisMissingDay[]
  years: AnalysisYear[]
  health: AnalysisHealth
  origins: AnalysisOrigin[]
  new_customers: { month: string; count: number }[]
  fixes: AnalysisFixes
}

// ---- months -----------------------------------------------------------------

// 'Sept', not 'Sep' — the wording the approved mockup uses everywhere a
// month is named. Own list, so ui.ts's 'Sep' spellings elsewhere are untouched.
export const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec']
const MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

const monthIndex = (m: string): number => parseInt(m.slice(5, 7), 10) - 1
const yearOf = (m: string): number => parseInt(m.slice(0, 4), 10)

export function monthShort(m: string): string {
  return MONTHS_SHORT[monthIndex(m)] ?? m
}

export function monthFull(m: string): string {
  return MONTHS_FULL[monthIndex(m)] ?? m
}

export function isCurrentMonth(m: string, today: string): boolean {
  return m === today.slice(0, 7)
}

// The bar's x-axis label: 'Feb', or 'Sept so far' for the month in progress.
export function barLabel(m: string, today: string): string {
  return isCurrentMonth(m, today) ? `${monthShort(m)} so far` : monthShort(m)
}

// 'Sept 2025 to now' — the range line beside the Month by month heading.
export function monthRangeSubtitle(months: AnalysisMonth[]): string {
  const first = months[0]
  if (!first) return ''
  return `${monthShort(first.month)} ${yearOf(first.month)} to now`
}

export function chartAriaLabel(months: AnalysisMonth[]): string {
  const first = months[0]
  const last = months[months.length - 1]
  if (!first || !last) return 'Income by month'
  return `Income by month, ${monthFull(first.month)} ${yearOf(first.month)} to ${monthFull(last.month)} ${yearOf(last.month)}`
}

// The last 13 months the chart draws (the payload carries 25; the owner reads
// a year, not two). Shorter payloads (a new round) just come through whole.
export function last13(months: AnalysisMonth[]): AnalysisMonth[] {
  return months.slice(-13)
}

// A month's day totals count as complete only when every worked day was
// filled in — that is the difference between a split bar and a plain one.
export function hasFullLedger(m: AnalysisMonth): boolean {
  return !!m.ledger && m.ledger.days_filled >= m.days_worked
}

// The four rows of "Where the money goes": the most recent months that have
// any day totals at all, oldest first — partial ones included, flagged by
// their amber pill.
export function ledgerRecent(months: AnalysisMonth[], n = 4): AnalysisMonth[] {
  return months.filter((m) => m.ledger !== null).slice(-n)
}

// ---- day by day -------------------------------------------------------------

// The days grouped under the month they belong to, newest month first and
// newest day first inside it, so a tax year reads as a list of months that
// each open into their days. A tax year is the unit that matters, but the
// close-outs only go back 25 months, so the grouping is by month and the
// totals are per month.
export interface AnalysisDayMonth {
  month: string
  days: AnalysisDay[]
  gross_pence: number
  worker_pence: number
  kept_pence: number
  cleans: number
}

// The ranges the day-totals table offers. A tax question is almost always
// "this month" or "the year so far"; anything older is a scroll, not a chip.
export type DayRange = 'month' | 'three' | 'year'
export const DAY_RANGE_LABELS: Record<DayRange, string> = {
  month: 'This month',
  three: 'Last three months',
  year: 'This year',
}

export function dayRangeStart(range: DayRange, today: string): string {
  if (range === 'year') return `${today.slice(0, 4)}-01-01`
  const months = range === 'month' ? 0 : 2
  const [y, m] = today.slice(0, 7).split('-').map(Number)
  const zero = y! * 12 + (m! - 1) - months
  return `${Math.floor(zero / 12)}-${String((zero % 12) + 1).padStart(2, '0')}-01`
}

export function inRange<T extends { date: string }>(rows: T[], range: DayRange, today: string): T[] {
  const from = dayRangeStart(range, today)
  return rows.filter((r) => r.date >= from)
}

// One line of the Day totals table and its download: a day total, a day the owner of a one-person round finished
// with Finish my day and entered no total (it counts as having one), or work with neither.
export type DayLine =
  | { kind: 'total'; date: string; row: AnalysisDay }
  | { kind: 'finished'; date: string; row: AnalysisMissingDay }
  | { kind: 'missing'; date: string; row: AnalysisMissingDay }

const LINE_ORDER = { total: 0, finished: 1, missing: 2 }

export function dayLines(data: AnalysisPayload, range: DayRange): DayLine[] {
  const totals: DayLine[] = inRange(data.days, range, data.today).map((row) => ({ kind: 'total', date: row.date, row }))
  const done: DayLine[] = inRange(data.finished_days ?? [], range, data.today).map((row) => ({ kind: 'finished', date: row.date, row }))
  const gaps: DayLine[] = inRange(data.missing_days ?? [], range, data.today).map((row) => ({ kind: 'missing', date: row.date, row }))
  // Newest first, and within one day the entered rows above the gaps.
  return [...totals, ...done, ...gaps].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : LINE_ORDER[a.kind] - LINE_ORDER[b.kind]))
}

// Where a one-person round shows worker pay (Liam, 19 Oct 2026: "B we want quality"; Sonnet's review): nowhere, unless
// the figures in view hold some, from days before the round went to one person. Then that section shows it as a team
// round does, so a Kept below the work done has its reason beside it. Each section asks of its own figures: the chart
// of its thirteen months, Where the money goes of its months, the Day totals of the days in its range; the opening
// line mentions workers when any of them does. A team round shows it everywhere.
export function paysWorkers(months: AnalysisMonth[]): boolean {
  return months.some((m) => (m.ledger?.worker_pence ?? 0) > 0)
}

export interface PayShown { chart: boolean; moneyGoes: boolean; days: boolean; any: boolean }

export function workerPayShown(data: AnalysisPayload, range: DayRange, alone: boolean): PayShown {
  if (!alone) return { chart: true, moneyGoes: true, days: true, any: true }
  const chart = paysWorkers(last13(data.months))
  const moneyGoes = paysWorkers(ledgerRecent(data.months))
  const days = dayLines(data, range).some((l) => l.kind === 'total' && l.row.worker_pence > 0)
  return { chart, moneyGoes, days, any: chart || moneyGoes || days }
}

// The Day totals columns. noPay: no worker pay to show (above), so no Worker or Paid worker; a team round's are
// exactly as before.
export function dayTotalsColumns(noPay: boolean): Array<[string, boolean]> {
  return noPay
    ? [['Day', false], ['Work done', true], ['Kept', true], ['Cleans', true], ['Note', false]]
    : [['Day', false], ['Worker', false], ['Work done', true], ['Paid worker', true], ['Kept', true], ['Cleans', true], ['Note', false]]
}

// "Download for the accountant": the same lines as the table, the same columns.
export function dayTotalsCsv(lines: DayLine[], noPay: boolean): string {
  const out = [noPay ? 'Date,Work done,Kept,Cleans,Note' : 'Date,Worker,Work done,Paid worker,Kept,Cleans,Note']
  const esc = (v: string): string => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v)
  const p = (pence: number): string => (pence / 100).toFixed(2)
  for (const line of lines) {
    if (line.kind === 'missing') {
      out.push((noPay
        ? [line.date, '', '', String(line.row.cleans), 'NO DAY TOTAL ENTERED']
        : [line.date, esc(line.row.worker || 'unassigned'), '', '', '', String(line.row.cleans), 'NO DAY TOTAL ENTERED']).join(','))
      continue
    }
    if (line.kind === 'finished') {
      // Finished with Finish my day: the work done is from the visits marked done; Kept comes only from a day total.
      const note = esc('Day finished, work done from the visits marked done')
      out.push((noPay
        ? [line.date, p(line.row.pence), '', String(line.row.cleans), note]
        : [line.date, esc(line.row.worker || 'unassigned'), p(line.row.pence), '', '', String(line.row.cleans), note]).join(','))
      continue
    }
    const d = line.row
    out.push((noPay
      ? [d.date, p(d.gross_pence), p(d.kept_pence), String(d.cleans), esc(d.notes ?? '')]
      : [d.date, esc(d.worker || 'unassigned'), p(d.gross_pence), p(d.worker_pence), p(d.kept_pence), String(d.cleans), esc(d.notes ?? '')]).join(','))
  }
  return out.join('\n')
}

// The line under the Day totals table. gaps: days with no total in the range; finished: days finished with Finish
// my day and no total (a one-person round only, so a team round always reads one of the first two).
export function dayTotalsNote(gaps: number, finished: number): string {
  if (finished > 0) {
    return `A day you finished with Finish my day counts as having its total; its work done is from the visits marked done. Kept and Where the money goes count only the day totals entered on the diary.${gaps ? ` ${gaps} ${gaps === 1 ? 'day is' : 'days are'} missing one, shown in amber.` : ''}`
  }
  return gaps
    ? `Every figure in this table is a day total entered on the diary. ${gaps} ${gaps === 1 ? 'day is' : 'days are'} missing one, shown in amber: that work is not in Kept or Where the money goes until the total is entered.`
    : 'Every figure in this table is a day total entered on the diary, and no day in this range is missing one.'
}

export function daysByMonth(days: AnalysisDay[]): AnalysisDayMonth[] {
  const out: AnalysisDayMonth[] = []
  const byMonth = new Map<string, AnalysisDayMonth>()
  // Newest first, which is the order the payload arrives in; sorting here
  // rather than trusting it keeps the view independent of the query.
  for (const d of [...days].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))) {
    const month = d.date.slice(0, 7)
    let group = byMonth.get(month)
    if (!group) {
      group = { month, days: [], gross_pence: 0, worker_pence: 0, kept_pence: 0, cleans: 0 }
      byMonth.set(month, group)
      out.push(group)
    }
    group.days.push(d)
    group.gross_pence += d.gross_pence
    group.worker_pence += d.worker_pence
    group.kept_pence += d.kept_pence
    group.cleans += d.cleans
  }
  return out
}

// "Mon 15 Sept" — the day named the way the diary names it, so the row and the
// day card he closed out read as the same day. The year is added when it is not
// this year, as the diary does: "oldest Thu 19 Sept" under Days missing a total
// was 19 Sept 2024 and read as last week (Liam's Dropby look-over, 29 Sep 2026).
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
export function dayLabelFull(iso: string, thisYear: number = new Date().getFullYear()): string {
  const [y, m, d] = iso.split('-').map(Number)
  const at = new Date(Date.UTC(y!, m! - 1, d!))
  return weekdayDayMonth(WEEKDAYS[at.getUTCDay()]!, d!, MONTHS_SHORT[m! - 1]!, y === thisYear ? undefined : y)
}

// ---- money ------------------------------------------------------------------

// Whole pounds with thousands separators: '£21,983'. Chart labels, tile
// values, table money that never carries pence (ui.money is the with-pence
// twin used where it does, like £63.50 average).
export function moneyNoPence(pence: number): string {
  return `${currencySymbol()}${Math.round(pence / 100).toLocaleString('en-GB')}`
}

// '72p' — kept pence in every £1 of gross. '—' when there was no gross.
export function penceInPound(kept: number, gross: number): string {
  if (gross <= 0) return '—'
  return `${Math.round((kept / gross) * 100)}${minorUnit()}`
}

export interface Pill {
  text: string
  kind: 'good' | 'bad' | null // null = plain grey
}

// '+36%' / '-12%' / '0%'. No pill at all when there is nothing to compare
// against (a division by zero would read as a real percentage).
export function pctPill(now: number, prev: number): Pill | null {
  if (prev <= 0) return null
  const pct = Math.round(((now - prev) / prev) * 100)
  return { text: pct > 0 ? `+${pct}%` : `${pct}%`, kind: pct > 0 ? 'good' : pct < 0 ? 'bad' : null }
}

// ---- chart scales -----------------------------------------------------------

// 1 / 2 / 2.5 / 5 × 10^n, rounded UP. The building block of both charts' axes.
export function niceStep(x: number): number {
  if (!(x > 0)) return 1
  const pow = Math.pow(10, Math.floor(Math.log10(x)))
  const f = x / pow
  const m = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10
  return m * pow
}

// Three divisions above the baseline (four gridlines counting it), with a
// little headroom so the value label above the tallest bar stays inside.
// The fixture's £3,799 max lands on step £2k / top £6k — the mockup's own scale.
export function chartScale(maxValue: number): { top: number; step: number } {
  const step = niceStep(maxValue / 3)
  return { step, top: step * 3 }
}

// '£2k' / '£2.5k' / '£500' — gridline labels.
export function gridLabel(value: number): string {
  const pounds = value / 100
  if (pounds >= 1000 && Number.isInteger(pounds / 1000)) return `${currencySymbol()}${pounds / 1000}k`
  if (pounds >= 1000 && Number.isInteger(pounds / 500)) return `${currencySymbol()}${pounds / 500 / 2}k`
  return `${currencySymbol()}${pounds.toLocaleString('en-GB')}`
}

// ---- dates ------------------------------------------------------------------

export function dayOfYear(iso: string): number {
  const [, mm, dd] = iso.split('-').map((n) => parseInt(n, 10))
  const cum = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
  let doy = dd
  for (let i = 0; i < mm - 1; i++) doy += cum[i]
  return doy
}

export function daysInYear(year: number): number {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 366 : 365
}

// ---- the sentences ----------------------------------------------------------

const plural = (n: number, one: string, many: string): string => (n === 1 ? one : many)

// 'on £16,147 at this point in 2025' — last year is derived from the payload's
// own today, never from the phone clock.
export function ytdSub(prevPence: number, today: string): string {
  return `on ${moneyNoPence(prevPence)} at this point in ${parseInt(today.slice(0, 4), 10) - 1}`
}

export function last12Sub(prevPence: number): string {
  return `on the 12 before (${moneyNoPence(prevPence)})`
}

// The Kept tile: '72p' / 'in every £1, after worker pay. Aug was 58p.'
// When this month has no day totals yet the value is '—' and the sub says so —
// an honest blank rather than a 0p that would read as "kept nothing".
// alone: a one-person round (Liam, 19 Oct 2026: "B we want quality"). Its months have no worker pay to be "after",
// except months from before it went to one person (Sonnet's review): a month that paid a worker says so, and so does
// last month's figure when it did and this month's did not, so a lower figure then has its reason.
export function keptTile(
  keptMonth: AnalysisKeptMonth | null,
  months: AnalysisMonth[],
  today: string,
  alone = false,
): { value: string; sub: string } {
  if (!keptMonth) return { value: '—', sub: 'no day totals yet this month' }
  const paid = !alone || keptMonth.worker_pence > 0
  let sub = paid ? `in every ${currencySymbol()}1, after worker pay.` : `in every ${currencySymbol()}1 of the day totals.`
  const prev = prevKeptSentence(months, today)
  const before = prevKeptMonth(months, today)
  if (prev && !paid && (before?.ledger?.worker_pence ?? 0) > 0) sub += ` ${prev.replace(/\.$/, ', after worker pay.')}`
  else if (prev) sub += ` ${prev}`
  return { value: penceInPound(keptMonth.kept_pence, keptMonth.gross_pence), sub }
}

// The nearest EARLIER month that has any day totals.
function prevKeptMonth(months: AnalysisMonth[], today: string): AnalysisMonth | null {
  const cur = today.slice(0, 7)
  for (let i = months.length - 1; i >= 0; i--) {
    const m = months[i]
    if (m.month < cur && m.ledger) return m
  }
  return null
}

// 'Aug was 58p.' — the nearest EARLIER month that has any day totals.
export function prevKeptSentence(months: AnalysisMonth[], today: string): string | null {
  const m = prevKeptMonth(months, today)
  return m?.ledger ? `${monthShort(m.month)} was ${penceInPound(m.ledger.kept_pence, m.ledger.gross_pence)}.` : null
}

export function owedSubLine(count: number): string {
  return `across ${count} ${plural(count, 'clean', 'cleans')}`
}

// 'February was the best month of the last year.' — the biggest gross of the
// thirteen on the chart. Null when there is nothing to compare.
export function bestMonthSentence(months: AnalysisMonth[]): string | null {
  let best: AnalysisMonth | null = null
  for (const m of months) {
    if (m.gross_pence > 0 && (!best || m.gross_pence > best.gross_pence)) best = m
  }
  return best ? `${monthFull(best.month)} was the best month of the last year.` : null
}

// 'Same point means 1 Jan to 12 Sept in both years.'
export function samePointNote(today: string): string {
  const [, mm, dd] = today.split('-').map((n) => parseInt(n, 10))
  return `Same point means ${dayMonth(1, 'Jan')} to ${dayMonth(dd, MONTHS_SHORT[mm - 1]!)} in both years.`
}

// 'On this pace 2026 finishes near £31,000.' — this year's money stretched
// over a whole year, to the nearest £1,000.
export function paceSentence(ytdPence: number, today: string): string | null {
  const doy = dayOfYear(today)
  if (doy <= 0) return null
  const year = parseInt(today.slice(0, 4), 10)
  const raw = (ytdPence / doy) * daysInYear(year)
  const rounded = Math.round(raw / 100_000) * 100_000
  return `On this pace ${year} finishes near ${moneyNoPence(rounded)}.`
}

// 'June: 26 cleans, £1,786, worker pay £710, kept £1,106' — the tap-a-bar
// line. A month whose day totals are missing or partial says so instead of
// quoting a kept figure that is not the month's. A one-person round's line has
// no worker pay, 'June: 26 cleans, £1,786, kept £1,106', unless that month paid
// a worker (before the round went to one person).
export function monthDetailLine(m: AnalysisMonth, today: string, alone = false): string {
  const label = isCurrentMonth(m.month, today) ? `${monthShort(m.month)} so far` : `${monthShort(m.month)} ${yearOf(m.month)}`
  const head = `${label}: ${m.cleans} ${plural(m.cleans, 'clean', 'cleans')}, ${moneyNoPence(m.gross_pence)}`
  if (!m.ledger || !hasFullLedger(m)) return `${head}, day totals not filled in`
  if (alone && !(m.ledger.worker_pence > 0)) return `${head}, kept ${moneyNoPence(m.ledger.kept_pence)}`
  return `${head}, worker pay ${moneyNoPence(m.ledger.worker_pence)}, kept ${moneyNoPence(m.ledger.kept_pence)}`
}

// The line under the Money heading: where each figure comes from. noPay: no worker pay anywhere on the page.
export function moneyLede(short: string, noPay: boolean): string {
  return noPay
    ? `What the round took, from the visits marked done. What ${short} kept, from the day totals on the diary.`
    : `What the round took, from the visits marked done. What the workers were paid and what ${short} kept, from the day totals on the diary.`
}

// The line under the Month by month chart, before the best-month sentence.
export function chartTapNote(short: string, noPay: boolean): string {
  return noPay ? `Tap a bar to see that month’s cleans and what ${short} kept. ` : `Tap a bar to see that month’s cleans, worker pay and ${short}’s cut. `
}

// The Where the money goes columns: no Worker pay column when its months paid no worker in a one-person round.
export function moneyGoesColumns(short: string, noPay: boolean): Array<[string, boolean]> {
  const cols: Array<[string, boolean]> = [['Month', false], ['Income', true], ['Worker pay', true], [`Kept by ${short}`, true], ['Kept', true], ['Days filled in', false]]
  return noPay ? cols.filter(([label]) => label !== 'Worker pay') : cols
}

// '61p' — kept per £1 averaged across the "Where the money goes" months.
// Totals first, then the ratio: an average of ratios would let a tiny month
// count as much as a big one.
export function avgKeptInPound(months: AnalysisMonth[]): string {
  let gross = 0
  let kept = 0
  for (const m of months) {
    if (!m.ledger) continue
    gross += m.ledger.gross_pence
    kept += m.ledger.kept_pence
  }
  return penceInPound(kept, gross)
}

export function moneyGoesNote(months: AnalysisMonth[], noPay = false): string {
  if (noPay) return `Over these months "Kept" averaged ${avgKeptInPound(months)} in the ${currencySymbol()}1.`
  return `"Kept" is what is left after the worker's cut. Over these months it averaged ${avgKeptInPound(months)} in the ${currencySymbol()}1.`
}

// 'Getting each customer one more clean a year on their cycle is worth about
// £5,600.' One more clean per book customer is one visit at their regular
// price, so the true figure is the SUM of regular prices over those
// customers. The payload does not carry that sum, so: the Work list's saved
// copy supplies it exactly when it has been loaded; otherwise this year's
// average clean price × the book customer count stands in; with neither, the
// sentence is omitted rather than guessed.
export function oneMoreCleanPence(health: AnalysisHealth, avgPence: number | null, workListSumPence: number | null): number | null {
  if (workListSumPence !== null && workListSumPence > 0) return workListSumPence
  if (avgPence !== null && avgPence > 0 && health.book_customers > 0) return avgPence * health.book_customers
  return null
}

export function oneMoreCleanSentence(health: AnalysisHealth, avgPence: number | null, workListSumPence: number | null): string | null {
  const worth = oneMoreCleanPence(health, avgPence, workListSumPence)
  if (worth === null) return null
  const rounded = Math.round(worth / 10_000) * 10_000 // nearest £100
  return `Getting each customer one more clean a year on their cycle is worth about ${moneyNoPence(rounded)}.`
}

// ---- origins ----------------------------------------------------------------

export function stillActivePct(o: AnalysisOrigin): number {
  if (o.customers <= 0) return 0
  return Math.round((o.active / o.customers) * 100)
}

// Green at 70%+ still active, red under 50%, plain in between; the mockup's
// cut-offs, and the same pair every other pill on the screen uses.
export function originPillKind(pct: number): 'good' | 'bad' | null {
  if (pct >= 70) return 'good'
  if (pct < 50) return 'bad'
  return null
}

// '53 customers have no source written down, so this table is missing 53 of
// 371.' The fraction is spelled as counts on purpose — "a fifth" stopped
// being true the moment the round grew.
export function originNote(origins: AnalysisOrigin[]): string | null {
  const nr = origins.find((o) => o.origin === 'Not recorded')
  if (!nr || nr.customers <= 0) return null
  const total = origins.reduce((sum, o) => sum + o.customers, 0)
  if (total <= 0) return null
  return `${nr.customers} customers have no source written down, so this table is missing ${nr.customers} of ${total}.`
}

// ---- things to fix ----------------------------------------------------------

export interface FixItem {
  title: string
  why: string
  action: { label: string; href: string; dark: boolean } | null
}

export function fixItems(
  fixes: AnalysisFixes,
  today: string,
  totalCustomers: number,
  missingDays?: AnalysisMissingDay[],
  settings: BusinessSettings = businessSettings(),
): FixItem[] {
  const out: FixItem[] = []
  if (fixes.no_cycle > 0) {
    out.push({
      title: `${fixes.no_cycle} active ${plural(fixes.no_cycle, 'customer', 'customers')} ${plural(fixes.no_cycle, 'has', 'have')} no cycle`,
      why: 'They never reach Messages, so nobody is reminded to rebook them. The biggest lever on the page.',
      action: { label: 'Open the list', href: '#/grid?view=nocycle', dark: true },
    })
  }
  if (fixes.no_origin > 0) {
    out.push({
      title: `${fixes.no_origin} ${plural(fixes.no_origin, 'customer', 'customers')} ${plural(fixes.no_origin, 'has', 'have')} no source`,
      why: `${fixes.no_origin} of ${totalCustomers} customers are missing from "where customers come from".`,
      action: { label: 'Open the list', href: '#/grid?view=noorigin', dark: false },
    })
  }
  if (fixes.unpriced_year > 0) {
    out.push({
      title: `${fixes.unpriced_year} ${plural(fixes.unpriced_year, 'clean', 'cleans')} this year ${plural(fixes.unpriced_year, 'has', 'have')} no price`,
      why: 'Income for those months reads low. (find them in the diary: a done tile with no price)',
      action: null,
    })
  }
  // Every day of work with no day total against it, not only this month's:
  // an unentered day from August is just as absent from the year's figures,
  // and the old wording let it stay invisible once the month turned. The
  // current-month count is the fallback when the caller has no list.
  const gaps = missingDays ?? []
  if (gaps.length > 0) {
    const oldest = gaps[gaps.length - 1]!
    out.push({
      title: `${gaps.length} ${plural(gaps.length, 'day', 'days')} of work ${plural(gaps.length, 'has', 'have')} no day total`,
      // The day as the tile above says it, never the computer's 2025-10-07 (bug hunt 8 Oct 2026).
      why: `That work is not in Kept or Where the money goes until the total is entered. The oldest is ${dayLabelFull(oldest.date)}.`,
      action: { label: 'Open the diary', href: `#/schedule?from=${oldest.date}`, dark: false },
    })
  } else if (!missingDays && fixes.ledger_missing_days > 0) {
    const mon = monthShort(today.slice(0, 7))
    out.push({
      title: `${fixes.ledger_missing_days} working ${plural(fixes.ledger_missing_days, 'day', 'days')} this month ${plural(fixes.ledger_missing_days, 'has', 'have')} no day totals`,
      why: `Until they are filled in, "kept by ${settings.short_name}" for ${mon} is only part of the month.`,
      action: { label: 'Fill in', href: '#/schedule', dark: false },
    })
  }
  return out
}
